import { fireEvent, screen, waitFor } from '@testing-library/react';
import { userEvent } from '@testing-library/user-event';
import { afterEach, beforeAll, describe, expect, it, vi } from 'vitest';

import { sendIntent, sendIntentWithResponse } from '@dynatrace-sdk/navigation';
import { render } from '@dynatrace/strato-components-testing/vitest';
import { PinIcon } from '@dynatrace/strato-icons';

import { IntentButton } from './IntentButton.js';

// mock navigation sdk
vi.mock('@dynatrace-sdk/navigation', async (importActual) => {
  const actual =
    await importActual<typeof import('@dynatrace-sdk/navigation')>();
  return {
    ...actual,
    sendIntent: vi.fn(),
    sendIntentWithResponse: vi.fn(),
    getIntentLink: vi
      .fn()
      .mockImplementation((_payload, appId?, intentId?) =>
        appId && intentId
          ? `http://mocked-intent-link/${appId}/${intentId}`
          : 'http://mocked-intent-link/',
      ),
  };
});

const intentPayload = { 'dt.query': 'fetch logs' };
const props = {
  payload: intentPayload,
  options: {
    recommendedAppId: 'dynatrace.notebooks',
    recommendedIntentId: 'view-query',
  },
};

const onResponse = vi.fn();

const withResponseProps = {
  payload: intentPayload,
  options: {
    recommendedAppId: 'dynatrace.notebooks',
    recommendedIntentId: 'view-query',
    responseProperties: [],
  },
  onResponse: onResponse,
};

describe('IntentButton', () => {
  beforeAll(() => {
    window.open = vi.fn() as typeof window.open;
  });

  afterEach(() => {
    vi.clearAllMocks();
  });

  describe('basic functionality', () => {
    it('should render the label', () => {
      render(<IntentButton {...props}>Custom label</IntentButton>);
      expect(screen.getByText('Custom label')).toBeInTheDocument();
    });

    it('should render a custom icon', () => {
      render(
        <IntentButton {...props}>
          Custom Label
          <IntentButton.Icon>
            <PinIcon data-testid="custom-icon" />
          </IntentButton.Icon>
        </IntentButton>,
      );

      expect(screen.getByTestId('custom-icon')).toBeInTheDocument();
    });

    it('should render a menu', () => {
      render(<IntentButton {...props}>Custom Label</IntentButton>);
      expect(screen.getByLabelText('Open intent menu')).toBeInTheDocument();
    });

    it('should render a menu with Item', async () => {
      render(
        <IntentButton {...props}>
          Custom Label
          <IntentButton.Item {...props}>Custom item</IntentButton.Item>
        </IntentButton>,
      );
      expect(screen.getByLabelText('Open intent menu')).toBeInTheDocument();
      await userEvent.click(screen.getByLabelText('Open intent menu'));
      expect(screen.getByText('Custom item')).toBeInTheDocument();
    });

    it('should render a menu with custom OpenWith', async () => {
      render(
        <IntentButton {...props}>
          Custom Label
          <IntentButton.OpenWith
            payload={{}}
            data-testid="open-with"
          ></IntentButton.OpenWith>
        </IntentButton>,
      );
      expect(screen.getByLabelText('Open intent menu')).toBeInTheDocument();
      await userEvent.click(screen.getByLabelText('Open intent menu'));
      expect(screen.getByTestId('open-with')).toHaveTextContent('Open with...');
    });

    it('should apply a default aria-label on the menu trigger', () => {
      render(<IntentButton {...props}>Custom Label</IntentButton>);
      expect(
        screen.getByRole('button', { name: 'Open intent menu' }),
      ).toBeTruthy();
    });
  });

  describe('intents', () => {
    describe('IntentButtonWithResponse', () => {
      it('should call sendIntentWithResponse when onResponse is passed', async () => {
        render(
          <IntentButton {...withResponseProps}>Custom Label</IntentButton>,
        );
        await userEvent.click(screen.getByText('Custom Label'));

        expect(sendIntentWithResponse).toHaveBeenNthCalledWith(
          1,
          intentPayload,
          {
            recommendedAppId: props.options.recommendedAppId,
            recommendedIntentId: props.options.recommendedIntentId,
            responseProperties: [],
          },
        );
      });

      it('should call sendIntentWithResponse on middle click', async () => {
        render(
          <IntentButton {...withResponseProps}>Custom Label</IntentButton>,
        );

        fireEvent.click(screen.getByText('Custom Label'), { button: 1 });
        await waitFor(() => {
          expect(sendIntentWithResponse).toHaveBeenNthCalledWith(
            1,
            intentPayload,
            {
              recommendedAppId: props.options.recommendedAppId,
              recommendedIntentId: props.options.recommendedIntentId,
              responseProperties: [],
            },
          );
        });
      });

      it('should call onResponse and return with success', async () => {
        const mockedSendIntentWithResponse = vi.mocked(sendIntentWithResponse);
        mockedSendIntentWithResponse.mockResolvedValueOnce({
          ok: true,
          data: { 'dt.share': 'test-return' },
        });

        render(
          <IntentButton {...withResponseProps}>Custom Label</IntentButton>,
        );
        await userEvent.click(screen.getByText('Custom Label'));
        expect(onResponse).toHaveBeenCalledWith({
          status: 'success',
          data: {
            response: { 'dt.share': 'test-return' },
          },
        });
      });

      it('should call onResponse and return with error', async () => {
        const mockedSendIntentWithResponse = vi.mocked(sendIntentWithResponse);
        mockedSendIntentWithResponse.mockImplementationOnce(() => {
          throw new Error();
        });

        render(
          <IntentButton {...withResponseProps}>Custom Label</IntentButton>,
        );
        await userEvent.click(screen.getByText('Custom Label'));
        expect(onResponse).toHaveBeenCalledWith({
          error: new Error('Error sending intent with response'),
          status: 'error',
        });
      });

      it('should call onResponse and return with success only on the clicked item', async () => {
        const onResponseItem = vi.fn();

        const mockedSendIntentWithResponse = vi.mocked(sendIntentWithResponse);
        mockedSendIntentWithResponse.mockResolvedValueOnce({
          ok: true,
          data: { 'dt.share': 'test-return' },
        });

        render(
          <IntentButton {...withResponseProps}>
            Custom Label
            <IntentButton.Item
              {...withResponseProps}
              onResponse={onResponseItem}
            >
              Custom Item
            </IntentButton.Item>
          </IntentButton>,
        );

        expect(screen.getByLabelText('Open intent menu')).toBeInTheDocument();
        await userEvent.click(screen.getByLabelText('Open intent menu'));
        await userEvent.click(screen.getByText('Custom Item'));

        expect(onResponse).not.toHaveBeenCalled();

        expect(onResponseItem).toHaveBeenCalledWith({
          status: 'success',
          data: {
            response: { 'dt.share': 'test-return' },
          },
        });
      });
    });
    describe('IntentButtonWithoutResponse', () => {
      it('should call sendIntent when no onResponse prop is passed', async () => {
        render(
          <IntentButton {...props} options={props.options}>
            Custom Label
          </IntentButton>,
        );
        await userEvent.click(screen.getByRole('link'));

        // Only one of the versions of the sendIntent functions is deprecated,
        // we are using it correctly here and can remove the disable lint
        // as soon as the deprecated function is removed from the sdk
        // oxlint-disable-next-line typescript/no-deprecated
        expect(sendIntent).toHaveBeenNthCalledWith(1, intentPayload, {
          recommendedAppId: props.options.recommendedAppId,
          recommendedIntentId: props.options.recommendedIntentId,
        });
      });

      it('should call intent.send on left click', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.click(screen.getByRole('link'), { button: 0 });
        // Only one of the versions of the sendIntent functions is deprecated,
        // we are using it correctly here and can remove the disable lint
        // as soon as the deprecated function is removed from the sdk
        // oxlint-disable-next-line typescript/no-deprecated
        expect(sendIntent).toHaveBeenCalled();
      });

      it('should not call intent.send on middle click', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.click(screen.getByRole('link'), { button: 1 });
        // Only one of the versions of the sendIntent functions is deprecated,
        // we are using it correctly here and can remove the disable lint
        // as soon as the deprecated function is removed from the sdk
        // oxlint-disable-next-line typescript/no-deprecated
        expect(sendIntent).not.toHaveBeenCalled();
      });

      it('should not call intent.send on ctrl+click', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.click(screen.getByRole('link'), { ctrlKey: true });
        // Only one of the versions of the sendIntent functions is deprecated,
        // we are using it correctly here and can remove the disable lint
        // as soon as the deprecated function is removed from the sdk
        // oxlint-disable-next-line typescript/no-deprecated
        expect(sendIntent).not.toHaveBeenCalled();
      });

      it('should call intent.send on Enter key', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.keyDown(screen.getByRole('link'), {
          code: 'Enter',
        });
        // Only one of the versions of the sendIntent functions is deprecated,
        // we are using it correctly here and can remove the disable lint
        // as soon as the deprecated function is removed from the sdk
        // oxlint-disable-next-line typescript/no-deprecated
        expect(sendIntent).toHaveBeenCalled();
      });

      it('should call window.open on ctrl+Enter', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.keyDown(screen.getByRole('link'), {
          code: 'Enter',
          ctrlKey: true,
        });
        expect(window.open).toHaveBeenCalledTimes(1);
      });

      it('should call window.open on shift+Space', async () => {
        render(<IntentButton {...props}>Custom Label</IntentButton>);
        fireEvent.keyDown(screen.getByRole('link'), {
          code: 'Space',
          shiftKey: true,
        });
        expect(window.open).toHaveBeenCalledTimes(1);
      });

      it('should set href intent link with an invalid appId', () => {
        const consoleSpy = vi
          .spyOn(console, 'error')
          .mockImplementation(vi.fn());

        const expectedHref = 'http://mocked-intent-link/';
        render(
          <IntentButton
            {...props}
            options={{ ...props.options, recommendedAppId: '' }}
          >
            Custom Label
          </IntentButton>,
        );
        expect(screen.getByRole('link').getAttribute('href')).toEqual(
          expectedHref,
        );

        consoleSpy.mockRestore();
      });

      it('should set href intent link with appId and intentId', () => {
        const expectedHref =
          'http://mocked-intent-link/dynatrace.notebooks/view-query';
        render(<IntentButton {...props}>Custom Label</IntentButton>);

        expect(screen.getByRole('link').getAttribute('href')).toEqual(
          expectedHref,
        );
      });

      it('should not set href when disabled', () => {
        render(
          <IntentButton {...props} options={{ ...props.options }} disabled>
            Custom Label
          </IntentButton>,
        );

        expect(screen.getByText('Custom Label').getAttribute('href')).toBe(
          null,
        );
      });
    });
  });

  describe('behavior tracking attributes', () => {
    it('should add data-dt-* attributes to the root element', () => {
      render(
        <IntentButton
          {...props}
          data-testid="intent-button"
          data-dt-content="tracking"
          data-dt-name="MyIntentButton"
          data-dt-children-name="IntentChildren"
        >
          Custom Label
        </IntentButton>,
      );

      const root = screen.getByTestId('intent-button');
      expect(root).toHaveAttribute('data-dt-content', 'tracking');
      expect(root).toHaveAttribute('data-dt-name', 'MyIntentButton');
      expect(root).toHaveAttribute('data-dt-children-name', 'IntentChildren');
    });

    describe('data-dt-component', () => {
      it('should not override the data-dt-component attribute', () => {
        const componentName = 'IntentComponent';
        render(
          <IntentButton
            {...props}
            data-testid="dtrum"
            data-dtrum-mask
            data-dtrum-allow
            data-dt-component={componentName}
          >
            Custom Label
          </IntentButton>,
        );

        const button = screen.getByTestId('dtrum');
        expect(button).toHaveAttribute('data-dt-component', 'IntentButton');
      });
    });

    describe('data-dtrum', () => {
      it('should set data-dtrum on the root level of the component', () => {
        render(
          <>
            <IntentButton
              {...props}
              data-testid="dtrum"
              data-dtrum-mask
              data-dtrum-allow
            >
              Custom Label
            </IntentButton>
            <IntentButton {...props} data-testid="no-dtrum">
              Custom Label
            </IntentButton>
          </>,
        );

        expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-mask');
        expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-allow');
        expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
          'data-dtrum-mask',
        );
        expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
          'data-dtrum-allow',
        );
      });

      it('should set data-dtrum on the menu', async () => {
        render(
          <>
            <IntentButton
              {...props}
              data-testid="dtrum"
              data-dtrum-mask
              data-dtrum-allow
            >
              Custom Label
            </IntentButton>
            <IntentButton {...props} data-testid="no-dtrum">
              Custom Label
            </IntentButton>
          </>,
        );

        await userEvent.click(screen.getAllByLabelText('Open intent menu')[0]);

        expect(screen.getByRole('menu')).toHaveAttribute('data-dtrum-mask');
        expect(screen.getByRole('menu')).toHaveAttribute('data-dtrum-allow');

        await userEvent.keyboard('{Escape}');
        await userEvent.click(screen.getAllByLabelText('Open intent menu')[1]);

        expect(screen.getByRole('menu')).not.toHaveAttribute('data-dtrum-mask');
        expect(screen.getByRole('menu')).not.toHaveAttribute(
          'data-dtrum-allow',
        );
      });
    });
  });

  describe('className & style', () => {
    it('should add consumer class names and styles', () => {
      render(
        <IntentButton
          {...props}
          data-testid="intent-button"
          className="test"
          style={{ backgroundColor: 'khaki' }}
        >
          Custom Label
        </IntentButton>,
      );

      const root = screen.getByTestId('intent-button');
      expect(root).toHaveClass('test');
      expect(root).toHaveStyle('background-color: rgb(240, 230, 140)');
    });
  });

  describe('aria props', () => {
    const cases = [
      {
        name: 'link (without-response)',
        renderProps: props,
        role: 'link' as const,
      },
      {
        name: 'button (with-response)',
        renderProps: withResponseProps,
        role: 'button' as const,
      },
    ];

    describe.each(cases)('$name', ({ renderProps, role }) => {
      it('should forward aria-label, aria-describedby, aria-details, and aria-disabled', () => {
        render(
          <>
            <span id="desc-id">Description</span>
            <span id="details-id">Details</span>
            <IntentButton
              {...renderProps}
              data-testid="intent-button"
              aria-label="Open notebooks"
              aria-describedby="desc-id"
              aria-details="details-id"
              aria-disabled
            >
              Custom Label
            </IntentButton>
          </>,
        );

        const el = screen.getByRole(role, { name: 'Open notebooks' });
        expect(el).toHaveAttribute('aria-label', 'Open notebooks');
        expect(el).toHaveAttribute('aria-describedby', 'desc-id');
        expect(el).toHaveAttribute('aria-details', 'details-id');
        expect(el).toHaveAttribute('aria-disabled', 'true');

        const root = screen.getByTestId('intent-button');
        expect(root).not.toHaveAttribute('aria-label');
        expect(root).not.toHaveAttribute('aria-describedby');
        expect(root).not.toHaveAttribute('aria-details');
        expect(root).not.toHaveAttribute('aria-disabled');
      });

      it('should forward aria-labelledby', () => {
        render(
          <>
            <span id="label-id">Open notebooks</span>
            <IntentButton
              {...renderProps}
              data-testid="intent-button"
              aria-labelledby="label-id"
            >
              Custom Label
            </IntentButton>
          </>,
        );

        expect(
          screen.getByRole(role, { name: 'Open notebooks' }),
        ).toHaveAttribute('aria-labelledby', 'label-id');
        expect(screen.getByTestId('intent-button')).not.toHaveAttribute(
          'aria-labelledby',
        );
      });
    });
  });

  describe('IntentButton.OpenWith behavior tracking', () => {
    it('should add data-dt-* attributes to IntentButton.OpenWith', async () => {
      render(
        <IntentButton {...withResponseProps} data-testid="intent-button">
          <IntentButton.OpenWith
            payload={intentPayload}
            data-dt-name="MyOpenWith"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          />
        </IntentButton>,
      );

      await userEvent.click(screen.getByLabelText('Open intent menu'));

      const openWithItem = document.querySelector(
        '[data-dt-name="MyOpenWith"]',
      );
      expect(openWithItem).not.toBeNull();
      expect(openWithItem).toHaveAttribute('data-dt-features', 'Feature1');
      expect(openWithItem).toHaveAttribute('data-dt-properties', 'key=value');
    });
  });

  describe('IntentButton.Item behavior tracking', () => {
    it('should add data-dt-* attributes to IntentButton.Item', async () => {
      render(
        <IntentButton {...props}>
          Custom Label
          <IntentButton.Item
            {...props}
            data-dt-name="MyItem"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          >
            Custom item
          </IntentButton.Item>
        </IntentButton>,
      );

      await userEvent.click(screen.getByLabelText('Open intent menu'));

      const item = document.querySelector('[data-dt-name="MyItem"]');
      expect(item).not.toBeNull();
      expect(item).toHaveAttribute('data-dt-features', 'Feature1');
      expect(item).toHaveAttribute('data-dt-properties', 'key=value');
    });
  });

  describe('IntentButton.Icon behavior tracking', () => {
    it('should add data-dt-* attributes to IntentButton.Icon', () => {
      render(
        <IntentButton {...props}>
          Custom Label
          <IntentButton.Icon
            data-dt-name="MyIcon"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          >
            <PinIcon data-testid="custom-icon" />
          </IntentButton.Icon>
        </IntentButton>,
      );

      const icon = document.querySelector('[data-dt-name="MyIcon"]');
      expect(icon).not.toBeNull();
      expect(icon).toHaveAttribute('data-dt-features', 'Feature1');
      expect(icon).toHaveAttribute('data-dt-properties', 'key=value');
    });
  });
});
